import type { WorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';

/** The nine steps of the HMS work, in order */
export const PROCESS_STEPS = [
  'draft',
  'sent',
  'risk',
  'data',
  'propose',
  'highlight',
  'plan',
  'followUp',
  'evaluation',
] as const;

export type ProcessStep = (typeof PROCESS_STEPS)[number];
export type StepState = 'done' | 'current' | 'notStarted';

const STEP_OF_STATUS: Record<WorkEnvironmentSurveyStatus, ProcessStep> = {
  [WorkEnvironmentSurveyStatus.DRAFT]: 'draft',
  [WorkEnvironmentSurveyStatus.SURVEY_SENT]: 'sent',
  [WorkEnvironmentSurveyStatus.SURVEY_IN_PROGRESS]: 'sent',
  [WorkEnvironmentSurveyStatus.AWAITING_RISK_CALC]: 'risk',
  [WorkEnvironmentSurveyStatus.RISK_CALCULATED]: 'risk',
  [WorkEnvironmentSurveyStatus.RISK_ASSESSED]: 'data',
  [WorkEnvironmentSurveyStatus.INSUFFICIENT_RESPONSES]: 'risk',
  [WorkEnvironmentSurveyStatus.ACTION_PROPOSAL_OPEN]: 'propose',
  [WorkEnvironmentSurveyStatus.ACTION_VOTING_OPEN]: 'highlight',
  [WorkEnvironmentSurveyStatus.ACTION_PLAN_PENDING]: 'plan',
  [WorkEnvironmentSurveyStatus.ACTION_PLAN_COMPLETED]: 'followUp',
  [WorkEnvironmentSurveyStatus.EVALUATING]: 'evaluation',
  [WorkEnvironmentSurveyStatus.ARCHIVED]: 'evaluation',
};

export const currentStepIndex = (status: WorkEnvironmentSurveyStatus) => PROCESS_STEPS.indexOf(STEP_OF_STATUS[status]);

export const stepState = (index: number, current: number): StepState =>
  index < current ? 'done' : index === current ? 'current' : 'notStarted';

/** The survey is out with the team, so the page shows who has answered */
export const isCollectingAnswers = (status: WorkEnvironmentSurveyStatus) => currentStepIndex(status) <= 1;

/** Why the survey has no step page: HR does not follow the answering, and older surveys ran before the HMS module */
export const unavailableReason = (status: WorkEnvironmentSurveyStatus) =>
  isCollectingAnswers(status) ? 'collecting' : 'beforeHms';

/**
 * Once the team has answered, the risk and the work after it are for those who follow it up.
 * A team member sees that, not a page they cannot open. Old answers without the flag show all.
 */
export const followsUpRisk = (survey: Pick<WorkEnvironmentSurvey, 'status' | 'canViewRisk'>) =>
  isCollectingAnswers(survey.status) || survey.canViewRisk !== false;

export const responsePercent = (responses: number, expected: number) =>
  expected > 0 ? Math.round((responses / expected) * 100) : 0;

const DAY_MS = 24 * 60 * 60 * 1000;

/** A started day counts as a day left */
export const daysLeft = (deadline: string, now: Date) =>
  Math.max(0, Math.ceil((new Date(deadline).getTime() - now.getTime()) / DAY_MS));

/** «5. oktober» */
export const dayAndMonth = (value: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', { day: 'numeric', month: 'long' }).format(
    new Date(value),
  );

export const surveyLink = (origin: string, uuid: string) => `${origin}/work-environment-survey/${uuid}`;
